const NARA_REFERRAL_STORAGE = 'nara.referral.code';
const NARA_REFERRAL_CODE_RE = /^[0-9A-HJ-NP-TV-Z]{8}$/;

function naraReferralNormalize(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
}

function naraReferralReadStored() {
  try {
    return naraReferralNormalize(window.sessionStorage.getItem(NARA_REFERRAL_STORAGE) || '');
  } catch {
    return '';
  }
}

function naraReferralWriteStored(code) {
  const normalized = naraReferralNormalize(code);
  if (!NARA_REFERRAL_CODE_RE.test(normalized)) return '';
  try {
    window.sessionStorage.setItem(NARA_REFERRAL_STORAGE, normalized);
  } catch {
    /* ignore quota */
  }
  return normalized;
}

function naraReferralCodeFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const query = naraReferralNormalize(params.get('code') || params.get('ref') || '');
  if (NARA_REFERRAL_CODE_RE.test(query)) return query;
  const match = window.location.pathname.match(/\/invite\/([0-9A-Za-z]+)/i);
  if (match) return naraReferralNormalize(match[1]);
  return '';
}

function naraReferralCaptureFromUrl() {
  const code = naraReferralCodeFromLocation() || naraReferralReadStored();
  if (code) naraReferralWriteStored(code);
  return naraReferralReadStored();
}

function naraReferralPrettyPath(code) {
  return `/invite/${code}`;
}

function naraReferralRedirectPrettyPath() {
  const match = window.location.pathname.match(/^\/invite\/([0-9A-Za-z]+)/i);
  if (!match) return false;
  const code = naraReferralNormalize(match[1]);
  if (!NARA_REFERRAL_CODE_RE.test(code)) return false;
  naraReferralWriteStored(code);
  if (!window.location.pathname.startsWith('/pages/invite')) {
    const next = `/pages/invite?code=${encodeURIComponent(code)}`;
    window.location.replace(next);
    return true;
  }
  return false;
}

function naraReferralConfig(root) {
  const url = (root?.dataset.supabaseUrl || '').replace(/\/$/, '');
  const key = root?.dataset.supabaseAnonKey || '';
  return { url, key, ready: Boolean(url && key) };
}

async function naraReferralRequest(config, path, { method = 'POST', body, token } = {}) {
  const headers = {
    apikey: config.key,
    Authorization: `Bearer ${token || config.key}`,
    'Content-Type': 'application/json',
  };
  const response = await fetch(`${config.url}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  return { ok: response.ok, status: response.status, payload };
}

function naraReferralSession() {
  try {
    const raw = window.localStorage.getItem('nara.auth.session');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function naraReferralFillInputs(code) {
  if (!code) return;
  document.querySelectorAll('[data-nara-referral-input]').forEach((input) => {
    if (!input.value) input.value = code;
  });
}

class NaraInviteLanding extends HTMLElement {
  connectedCallback() {
    this.config = naraReferralConfig(this);
    this.status = this.querySelector('[data-nara-invite-status]');
    this.form = this.querySelector('form');
    this.abort = new AbortController();
    const code = naraReferralCaptureFromUrl();
    naraReferralFillInputs(code);
    this.form?.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.abort.signal });
    this.querySelectorAll('[data-nara-auth-toggle]').forEach((toggle) => {
      toggle.addEventListener(
        'click',
        () => {
          const field = toggle.closest('.nara-auth__password')?.querySelector('[data-nara-auth-password]');
          if (!field) return;
          const hidden = field.type === 'password';
          field.type = hidden ? 'text' : 'password';
          toggle.setAttribute('aria-pressed', hidden ? 'true' : 'false');
          toggle.textContent = hidden ? 'Hide' : 'Show';
        },
        { signal: this.abort.signal },
      );
    });
    this.lookup(code);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  show(message, tone) {
    if (!this.status) return;
    this.status.hidden = !message;
    this.status.textContent = message || '';
    this.status.dataset.tone = tone || 'info';
  }

  async lookup(code) {
    if (!code) {
      this.show('Add a referral code if you have one. The trial on annual plans is the standard one-month trial.', 'info');
      return;
    }
    if (!this.config.ready) return;
    const { ok, payload } = await naraReferralRequest(
      this.config,
      `/functions/v1/nara-referral?action=lookup&code=${encodeURIComponent(code)}`,
      { method: 'GET' },
    );
    if (ok && payload?.valid) {
      this.show('This invite is valid. Create your Nara account to start. Your friend’s reward is applied only after your first confirmed annual payment.', 'success');
    } else {
      this.show('This invite code is not valid. You can still create an account and enter another code.', 'error');
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.config.ready) {
      this.show('Account creation isn’t connected on this website yet.', 'error');
      return;
    }
    const form = this.form;
    const email = form.elements.email.value.trim();
    const password = form.elements.password.value;
    const confirm = form.elements.confirm?.value;
    const code = naraReferralWriteStored(form.elements.referral?.value || naraReferralReadStored());
    if (confirm && confirm !== password) {
      this.show('The passwords do not match.', 'error');
      return;
    }
    const submit = form.querySelector('[data-nara-invite-submit]');
    if (submit) {
      submit.disabled = true;
      submit.dataset.label = submit.textContent;
      submit.textContent = 'Creating account…';
    }
    try {
      const { ok, payload } = await naraReferralRequest(this.config, '/auth/v1/signup', {
        body: {
          email,
          password,
          data: code ? { referral_code: code } : {},
        },
      });
      if (!ok || payload?.error || payload?.msg) {
        this.show(payload?.msg || 'We couldn’t create this account. Try again.', 'error');
        return;
      }
      if (payload?.access_token) {
        window.localStorage.setItem('nara.auth.session', JSON.stringify(payload));
        if (code) {
          await naraReferralRequest(this.config, '/functions/v1/nara-referral', {
            token: payload.access_token,
            body: { action: 'claim', code },
          });
        }
        window.location.assign('/pages/my-nara');
        return;
      }
      this.show(
        'Check your email to confirm this account. Your invite code is saved on this device until you finish sign-in.',
        'success',
      );
    } catch {
      this.show('We couldn’t create this account. Try again.', 'error');
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.label || 'Create account';
      }
    }
  }
}

class NaraReferralSpace extends HTMLElement {
  connectedCallback() {
    this.config = naraReferralConfig(this);
    this.abort = new AbortController();
    this.querySelector('[data-nara-referral-copy]')?.addEventListener('click', () => this.copy(), {
      signal: this.abort.signal,
    });
    this.querySelector('[data-nara-referral-share]')?.addEventListener('click', () => this.share(), {
      signal: this.abort.signal,
    });
    this.load();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  setText(selector, value) {
    const node = this.querySelector(selector);
    if (node) node.textContent = value;
  }

  async load() {
    const session = naraReferralSession();
    if (!session?.access_token || !this.config.ready) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    const code = naraReferralReadStored();
    if (code) {
      await naraReferralRequest(this.config, '/functions/v1/nara-referral', {
        token: session.access_token,
        body: { action: 'claim', code },
      });
    }
    const { ok, payload } = await naraReferralRequest(this.config, '/functions/v1/nara-referral', {
      token: session.access_token,
      body: { action: 'dashboard' },
    });
    if (!ok || !payload) {
      this.setText('[data-nara-referral-note]', 'Your invite details could not be loaded.');
      return;
    }
    this.dashboard = payload;
    this.render(payload);
  }

  render(data) {
    const input = this.querySelector('[data-nara-referral-link]');
    if (input) input.value = data.invite_url || '';
    this.setText('[data-nara-referral-months]', String(data.months_earned ?? 0));
    this.setText('[data-nara-referral-pending]', String(data.pending_rewards ?? 0));

    const eligible = this.querySelector('[data-nara-referral-eligible]');
    const ineligible = this.querySelector('[data-nara-referral-ineligible]');
    if (eligible) eligible.hidden = !data.eligible;
    if (ineligible) ineligible.hidden = Boolean(data.eligible);
    if (!data.eligible) {
      this.setText('[data-nara-referral-eligibility]', data.eligibility_reason || '');
    }

    const limitation = this.querySelector('[data-nara-referral-limitation]');
    if (limitation) {
      limitation.hidden = Boolean(data.reward_apply_supported);
      limitation.textContent = data.reward_apply_limitation || '';
    }

    const delay = this.querySelector('[data-nara-referral-delay]');
    if (delay) {
      const seconds = Number(data.reward_apply_delay_seconds || 0);
      delay.hidden = seconds <= 0;
      delay.textContent =
        seconds > 0
          ? `Confirmed rewards stay pending for ${seconds} seconds before an apply attempt. This delay is the configured NARA_REFERRAL_REWARD_APPLY_DELAY_SECONDS / settings value.`
          : '';
    }

    const renewal = this.querySelector('[data-nara-referral-renewal]');
    if (renewal) {
      if (data.next_renewal_confirmed && data.next_renewal_at) {
        renewal.hidden = false;
        renewal.textContent = `Next confirmed renewal: ${new Date(data.next_renewal_at).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })}`;
      } else {
        renewal.hidden = true;
        renewal.textContent = '';
      }
    }

    const list = this.querySelector('[data-nara-referral-history]');
    if (list) {
      list.replaceChildren();
      (data.history || []).forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item.label || item.status;
        list.appendChild(li);
      });
      if (!data.history?.length) {
        const li = document.createElement('li');
        li.textContent = 'No invites have a status yet.';
        list.appendChild(li);
      }
    }
  }

  async copy() {
    const value = this.dashboard?.invite_url;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      this.setText('[data-nara-referral-copied]', 'Invite link copied.');
    } catch {
      this.setText('[data-nara-referral-copied]', 'Copy the link from the field.');
    }
  }

  async share() {
    const value = this.dashboard?.invite_url;
    if (!value) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Nara',
          text: 'Your friend gets a 1-month trial on an annual plan. You earn an extra month after their first annual payment.',
          url: value,
        });
      } catch {
        /* user cancelled */
      }
      return;
    }
    this.copy();
  }
}

if (!customElements.get('nara-invite-landing')) {
  customElements.define('nara-invite-landing', NaraInviteLanding);
}
if (!customElements.get('nara-referral-space')) {
  customElements.define('nara-referral-space', NaraReferralSpace);
}

function naraReferralBoot() {
  if (naraReferralRedirectPrettyPath()) return;
  naraReferralCaptureFromUrl();
  naraReferralFillInputs(naraReferralReadStored());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', naraReferralBoot);
} else {
  naraReferralBoot();
}
